import {
  Table,
  TableCell,
  TableHead,
  Typography,
  CircularProgress,
  Stack,
  TableBody,
  Button,
  Box,
  Tooltip,
  IconButton,
  TableRow,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo } from 'react';
import { InvoiceState, columns } from './Invoices.const';
import { StyledTableRow } from '../teamMemberView/StyledTableRow';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { getSelectedAccount } from '../../redux/account';
import { useTitle } from '../../hooks/useTitle';
import { PageTitle } from '../../utils/pageTitles';
import { useParams } from 'react-router-dom';
import {
  Invoice,
  fetchProjectInvoicesData,
  invoicesSlice,
  selectinvoicesData,
  getSort,
  sortableColumns,
  SortInvoicesBy,
} from '../../redux/invoices';
import { DataState } from '../../types/asyncData';
import { InvoiceDataRow } from './components/InvoiceDataRow';
import { InvoicesFilter } from './components/InvoicesFilter';

import FilterListIcon from '@mui/icons-material/FilterList';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { SortOrder } from '../../types';
import InfoIcon from '@mui/icons-material/Info';
import ArrowUpIcon from '@mui/icons-material/ArrowUpward';
import { downloadCsv } from '../reports/Reports.utils';
import Papaparse from 'papaparse';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

export const Invoices = () => {
  const dispatch = useAppDispatch();
  const account = useAppSelector(getSelectedAccount);
  const { accountId } = useParams();
  const sort = useAppSelector(getSort);

  const invoicesPageData = useAppSelector(selectinvoicesData);
  const {
    areFiltersApplied,
    filterBy: { invoiceState, projectNames, showZeroInvoices },
  } = invoicesPageData;

  const numberOfFiltersApplied = useMemo(
    () =>
      (invoiceState === InvoiceState.ALL ? 0 : 1) +
      (projectNames.length > 0 ? 1 : 0) +
      (showZeroInvoices ? 1 : 0),
    [invoiceState, projectNames, showZeroInvoices],
  );

  const handleColumnSort = useCallback(
    (columnName: string) => {
      const newSortBy = sortableColumns[columnName];
      if (newSortBy) {
        dispatch(invoicesSlice.actions.upsertToSort({ newSortBy: newSortBy }));
      }
    },
    [dispatch],
  );

  const filterInvoicesByState = useCallback(
    (invoices: Invoice[], invoiceState: InvoiceState) => {
      const currentDate = new Date();

      switch (invoiceState) {
        case InvoiceState.ALL:
          return invoices;
        case InvoiceState.OVERDUE:
          return invoices.filter(
            (invoice) =>
              !invoice.paidOn && new Date(invoice.invoiceDueDate) < currentDate,
          );
        case InvoiceState.OPEN:
          return invoices.filter(
            (invoice) =>
              new Date(invoice.invoiceDueDate) > currentDate && !invoice.paidOn,
          );
        case InvoiceState.PAIDAFTERDUE:
          return invoices.filter((invoice) => invoice.isPaidAfterPastDue);
        case InvoiceState.PAID:
          return invoices.filter(
            (invoice) => invoice.paidOn && !invoice.isPaidAfterPastDue,
          );
        default:
          return invoices;
      }
    },
    [],
  );

  const invoicesData = useMemo(() => {
    let invoices = invoicesPageData.invoices.data ?? [];

    if (!invoices.length) return [];

    // Filter by project names if provided
    if (projectNames.length) {
      invoices = invoices.filter(
        (invoice: Invoice) =>
          invoice.projectName && projectNames.includes(invoice.projectName),
      );
    }

    // Filter invoices based on the selected invoice state
    let filteredInvoices = filterInvoicesByState(invoices, invoiceState);

    // Filter out $0 invoices if unchecked
    if (!showZeroInvoices) {
      filteredInvoices = filteredInvoices.filter(
        (invoice: Invoice) => invoice.amount !== 0,
      );
    }

    return filteredInvoices;
  }, [
    invoiceState,
    projectNames,
    showZeroInvoices,
    invoicesPageData,
    filterInvoicesByState,
  ]);

  const invoicesDataState = invoicesPageData.invoices.state;
  const setPageTitle = useTitle();

  useEffect(() => {
    setPageTitle(
      `${account?.name ?? PageTitle.portfolio} | ${PageTitle.invoices}`,
    );
  }, [setPageTitle, account]);

  useEffect(() => {
    if (accountId) {
      dispatch(
        fetchProjectInvoicesData({
          accountId: accountId,
        }),
      );
    }
    return () => {
      dispatch(invoicesSlice.actions.resetFilters(InvoiceState.OVERDUE));
      dispatch(invoicesSlice.actions.resetInvoices());
    };
  }, [dispatch, accountId]);

  const hadleOpenFilterDrawer = useCallback(
    () => dispatch(invoicesSlice.actions.openFilter()),
    [dispatch],
  );

  const resetFilters = useCallback(() => {
    dispatch(invoicesSlice.actions.resetFilters(InvoiceState.ALL));
  }, [dispatch]);

  if (invoicesDataState === DataState.Pending) {
    return (
      <Stack
        sx={{
          backgroundColor: 'white',
          border: '1px solid rgba(0, 0, 0, 0.12)',
          borderTop: 'none',
          borderRadius: '8px',
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
          height: '600px',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress color="secondary" />
      </Stack>
    );
  }

  return (
    <>
      <Stack
        width="100%"
        sx={{
          border: '1px solid #e0e0e0',
          height: 'fit-content',
          borderTop: 'none',
          borderRadius: '0px',
          borderBottomLeftRadius: '4px',
          borderBottomRightRadius: '4px',
          backgroundColor: 'white',
        }}
      >
        <Box display="flex" alignItems={'center'} padding={2} marginBottom={0}>
          <Typography variant="h3">Invoices</Typography>

          <Stack flexDirection="row" gap={1} ml="auto" alignItems="center">
            <Tooltip title="If multiple sorting is applied, the invoices will be sorted by the following priority: Due date, Amount, Paid on, Debt, Paid, Is Paid After Past Due, Project name, Invoice id">
              <IconButton>
                <InfoIcon />
              </IconButton>
            </Tooltip>

            <InvoicesCsvDownloadButton invoices={invoicesData} />
            <Button
              variant="contained"
              onClick={hadleOpenFilterDrawer}
              {...{ color: areFiltersApplied ? 'secondary' : 'primary' }}
              size="small"
            >
              <FilterListIcon sx={{ fontSize: '18px' }} />
              &nbsp; Filters
              {areFiltersApplied && `: ${numberOfFiltersApplied}`}
            </Button>

            <Button
              variant="text"
              onClick={resetFilters}
              sx={{
                ...(!areFiltersApplied && {
                  display: 'none',
                  visibility: 'hidden',
                }),
                backgroundColor: '#9c27b020',
              }}
              {...(areFiltersApplied && {
                color: 'secondary',
              })}
              size="small"
            >
              <RemoveCircleOutlineIcon
                fill="#448aff"
                sx={{ fontSize: '18px' }}
              />
              &nbsp; Reset
            </Button>
          </Stack>
        </Box>

        <InvoicesFilter />
        <Table
          aria-label="collapsible table"
          stickyHeader
          sx={{ minWidth: 'max-content' }}
        >
          <TableHead>
            <TableRow>
              {columns.map((columnName: string) => (
                <SortableHeaderColumn
                  columnName={columnName}
                  key={columnName}
                  sort={sort}
                  handleColumnSort={handleColumnSort}
                />
              ))}
            </TableRow>
          </TableHead>
          <TableBody
            sx={{
              'tr:not(:last-child, .projectRow)': {
                boxShadow: 'inset 0px -1px 0px rgba(0, 0, 0, 0.12)',
              },
              '.projectRow': {
                position: 'sticky',
                top: '56px',
                zIndex: 2,
              },
            }}
          >
            {invoicesData && invoicesData.length ? (
              invoicesData.map((invoice: Invoice) => (
                <InvoiceDataRow data={invoice} key={invoice.id} />
              ))
            ) : (
              <StyledTableRow isLastRow={true} isHidden={true}>
                <TableCell align="left" colSpan={8}>
                  <Typography
                    whiteSpace="nowrap"
                    textAlign="left"
                    variant="h4"
                    color="slategray"
                  >
                    {`${
                      areFiltersApplied
                        ? 'No invoices are available in this account for the selected filters.'
                        : 'No overdue invoices are available in this account.'
                    }`}
                  </Typography>
                </TableCell>
              </StyledTableRow>
            )}
          </TableBody>
        </Table>
      </Stack>
    </>
  );
};

const SortableHeaderColumn = ({
  columnName,
  sort,
  handleColumnSort,
}: {
  columnName: string;
  sort: {
    by: SortInvoicesBy;
    order: SortOrder;
  }[];
  handleColumnSort: (columnName: string) => void;
}) => {
  const sortElement = useMemo(() => {
    return sort.find((s) => s.by === sortableColumns[columnName]);
  }, [columnName, sort]);

  const sortOrder = sortElement?.order;
  const sortBy = sortElement?.by;
  const isSorted = sortBy === sortableColumns[columnName];

  return (
    <TableCell
      key={columnName}
      variant="head"
      align={['Amount', 'Debt', 'Paid'].includes(columnName) ? 'right' : 'left'}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: ['Amount', 'Debt', 'Paid'].includes(columnName)
            ? 'flex-end'
            : 'flex-start',
          '&:hover .sort-icon': {
            opacity: 0.5,
          },
          ...(isSorted && {
            '&:hover .sort-icon': {
              opacity: 1,
            },
          }),
        }}
        onClick={() => handleColumnSort(columnName)}
      >
        {['Amount', 'Debt', 'Paid'].includes(columnName) && (
          <IconButton
            size="small"
            className="sort-icon" // 1. Add identifier class
            sx={{
              opacity: isSorted ? 1 : 0,
              transition: 'opacity 0.2s ease-in-out',
              color: isSorted ? 'primary' : 'disabled',
            }}
          >
            <ArrowUpIcon
              fontSize="small"
              sx={{
                transform:
                  sortOrder === SortOrder.Ascending
                    ? 'rotate(0deg)'
                    : 'rotate(180deg)',
              }}
            />
          </IconButton>
        )}

        <Typography
          sx={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
          variant="h4"
          component="p"
        >
          {columnName}
        </Typography>

        {!['Amount', 'Debt', 'Paid'].includes(columnName) && (
          <IconButton
            size="small"
            className="sort-icon" // 1. Add identifier class
            sx={{
              opacity: isSorted ? 1 : 0,
              transition: 'opacity 0.2s ease-in-out',
              color: isSorted ? 'primary' : 'disabled',
            }}
          >
            <ArrowUpIcon
              fontSize="small"
              sx={{
                transform:
                  sortOrder === SortOrder.Ascending
                    ? 'rotate(0deg)'
                    : 'rotate(180deg)',
              }}
            />
          </IconButton>
        )}
      </Box>
    </TableCell>
  );
};

interface InvoicesCsvDownloadButtonProps {
  invoices: Invoice[];
}

export const InvoicesCsvDownloadButton = ({
  invoices,
}: InvoicesCsvDownloadButtonProps) => {
  const handleInvoicesExport = useCallback(async () => {
    if (!invoices || invoices.length === 0) return;

    const csvData = invoices.map((row) => {
      return {
        ID: row.id,
        'Invoice URL': `${row.invoiceUrl}`,
        Amount: row.amount,
        Debt: row.debt,
        'Amount Paid': row.amountPaid,
        'Invoice Due Date': row.invoiceDueDate,
        'Invoice Sent': row.whencreated,
        'Paid On': row.paidOn,
        'Is Paid After Past Due': row.isPaidAfterPastDue,
        'Project Name': row.projectName,
      };
    });

    const csv = Papaparse.unparse(csvData, { quotes: true });
    downloadCsv(csv, 'invoices.csv');
  }, [invoices]);

  return (
    <Button
      variant="text"
      size="small"
      sx={{ mx: 2 }}
      onClick={handleInvoicesExport}
    >
      <FileDownloadIcon
        sx={{
          color: '#000000',
          fontSize: '18px',
        }}
      />
      <Typography variant="body1" color="text.primary" sx={{ marginLeft: 0.7 }}>
        Export
      </Typography>
    </Button>
  );
};
